// Flash Report View Component
// Displays the last 13 completed weeks of data with totals and averages
// The current (selected) week is shown in the table but excluded from totals/averages
// because plants often finalize numbers mid-week after the Saturday deadline

import React, { useState, useEffect } from 'react';
import { flashReportAPI } from '../utils/api';
import { formatDateForDisplay, formatDateForInput, getMostRecentSaturday, isSaturday } from '../utils/dateHelpers';
import '../styles/FlashReportView.css';

function FlashReportView() {
    const [plants, setPlants] = useState([]);
    const [selectedPlant, setSelectedPlant] = useState('');
    const [endingDate, setEndingDate] = useState('');
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });

    // Load plants when component mounts
    useEffect(() => {
        loadPlants();
        // Set default ending date to most recent Saturday
        setEndingDate(formatDateForInput(getMostRecentSaturday()));
    }, []);

    // Load report data when plant or ending date changes
    useEffect(() => {
        if (selectedPlant && endingDate) {
            loadReportData();
        }
    }, [selectedPlant, endingDate]);

    // Load plants from API
    const loadPlants = async () => {
        try {
            const data = await flashReportAPI.getPlants();
            setPlants(data.plants);

            // Auto-select Seattle if available
            const seattle = data.plants.find(p => p.plant_name === 'Seattle');
            if (seattle) {
                setSelectedPlant(seattle.plant_id.toString());
            }
        } catch (error) {
            setMessage({ text: 'Error loading plants', type: 'error' });
        }
    };

    // Handle ending date change with Saturday validation
    const handleEndingDateChange = (e) => {
        const selectedDate = e.target.value;

        // Check if the selected date is a Saturday
        if (!isSaturday(selectedDate)) {
            setMessage({
                text: 'Ending date must be a Saturday. Please select a Saturday.',
                type: 'error'
            });
            return;
        }

        setEndingDate(selectedDate);
        setMessage({ text: '', type: '' });
    };

    // Load report data for selected plant ending on selected date
    // The backend now returns 14 rows: 1 current week + 13 completed weeks
    const loadReportData = async () => {
        if (!selectedPlant || !endingDate) return;

        try {
            setLoading(true);
            setMessage({ text: '', type: '' });

            const data = await flashReportAPI.getHistory(selectedPlant, 13, endingDate);

            if (data.reports && data.reports.length > 0) {
                setReportData(data.reports);
            } else {
                setReportData([]);
                setMessage({ text: 'No data available for this plant and date range', type: 'info' });
            }
        } catch (error) {
            setMessage({ text: 'Error loading report data', type: 'error' });
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // COMPLETED WEEKS ONLY
    // The backend flags the first row (current/selected week) with is_current_week = true
    // We filter those out before calculating totals and averages so that an
    // unfinished week does not drag down the numbers
    // ─────────────────────────────────────────────────────────────────────────
    const completedWeeks = reportData.filter(row => !row.is_current_week);

    // Calculate totals using only the 13 completed weeks
    const calculateTotals = () => {
        if (completedWeeks.length === 0) return null;

        return {
            clean_lbs:    completedWeeks.reduce((sum, row) => sum + (parseFloat(row.clean_lbs)    || 0), 0),
            soil_lbs:     completedWeeks.reduce((sum, row) => sum + (parseFloat(row.soil_lbs)     || 0), 0),
            revenue:      completedWeeks.reduce((sum, row) => sum + (parseFloat(row.revenue)      || 0), 0),
            direct_hours: completedWeeks.reduce((sum, row) => sum + (parseFloat(row.direct_hours) || 0), 0),
            direct_labor: completedWeeks.reduce((sum, row) => sum + (parseFloat(row.direct_labor) || 0), 0),
            total_hours:  completedWeeks.reduce((sum, row) => sum + (parseFloat(row.total_hours)  || 0), 0),
            total_labor:  completedWeeks.reduce((sum, row) => sum + (parseFloat(row.total_labor)  || 0), 0),
            ot_hours:     completedWeeks.reduce((sum, row) => sum + (parseFloat(row.total_ot_hours)|| 0), 0),

            // PM tickets (FMX). Only weeks that actually carry a figure are
            // counted. A NULL week means the plant raised no maintenance
            // tickets — it has no completion rate, so folding it in as a zero
            // would understate every plant that simply had a quiet week.
            pm_total:     completedWeeks.reduce((sum, row) =>
                              sum + (row.pm_tickets_total     !== null ? (parseInt(row.pm_tickets_total)     || 0) : 0), 0),
            pm_completed: completedWeeks.reduce((sum, row) =>
                              sum + (row.pm_tickets_completed !== null ? (parseInt(row.pm_tickets_completed) || 0) : 0), 0)
        };
    };

    // Calculate averages using only the 13 completed weeks
    const calculateAverages = () => {
        if (completedWeeks.length === 0) return null;

        const totals = calculateTotals();
        const count  = completedWeeks.length;

        // For soil_lbs, only average over weeks that actually have data
        // since it is a newer field and older weeks may be 0 or null
        const soilCount = completedWeeks.filter(r =>
            r.soil_lbs !== null && parseFloat(r.soil_lbs) > 0
        ).length;

        return {
            clean_lbs:    totals.clean_lbs    / count,
            soil_lbs:     soilCount > 0 ? totals.soil_lbs / soilCount : 0,
            revenue:      totals.revenue      / count,
            direct_hours: totals.direct_hours / count,
            direct_labor: totals.direct_labor / count,
            total_hours:  totals.total_hours  / count,
            total_labor:  totals.total_labor  / count,
            ot_hours:     totals.ot_hours     / count,

            // POOLED, not the mean of the weekly percentages. The weekly
            // denominators are small and uneven — a week with 2 tickets and a
            // week with 38 would otherwise count equally. NULL when the plant
            // raised nothing across the whole period.
            pm_completion_pct: totals.pm_total > 0
                ? (totals.pm_completed / totals.pm_total) * 100
                : null
        };
    };

    // One PM cell: the percentage over the raw counts. Never renders 0% for a
    // week with no tickets — that reads as "completed nothing" rather than
    // "nothing to complete".
    const renderPmCell = (completed, total) => {
        if (total === null || total === undefined || total === 0) {
            return <span className="pm-nodata" title="No maintenance tickets raised">-</span>;
        }
        return (
            <>
                <span className="pm-pct">{((completed / total) * 100).toFixed(1)}%</span>
                <span className="pm-counts">{completed} of {total}</span>
            </>
        );
    };

    // Format number with commas
    const formatNumber = (num, decimals = 0) => {
        if (num === null || num === undefined) return '-';
        return parseFloat(num).toLocaleString('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };

    // Handle Excel export
    const handleExportExcel = async () => {
        if (!selectedPlant || !endingDate) {
            setMessage({ text: 'Please select a plant and date', type: 'error' });
            return;
        }

        try {
            setLoading(true);
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/export/flash-13week/${selectedPlant}/${endingDate}`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Export failed');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Flash_13Week_${endingDate}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            setMessage({ text: 'Report exported successfully', type: 'success' });
        } catch (error) {
            console.error('Export error:', error);
            setMessage({ text: 'Error exporting report', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const totals           = calculateTotals();
    const averages         = calculateAverages();
    const selectedPlantName = plants.find(p => p.plant_id.toString() === selectedPlant)?.plant_name || '';

    return (
        <div className="flash-report-view-container">
            <div className="report-header">
                <h2>Flash Report - 13 Week View</h2>

                <div className="header-controls-wrapper">
                    <div className="report-actions">
                        <button
                            onClick={() => window.print()}
                            className="btn-print"
                            disabled={loading || !reportData || reportData.length === 0}
                        >
                            🖨️ Print
                        </button>
                        <button
                            onClick={handleExportExcel}
                            className="btn-export"
                            disabled={loading || !reportData || reportData.length === 0}
                        >
                            📊 Export to Excel
                        </button>
                    </div>

                    <div className="report-controls">
                        <div className="form-group">
                            <label htmlFor="reportPlant">Plant:</label>
                            <select
                                id="reportPlant"
                                value={selectedPlant}
                                onChange={(e) => setSelectedPlant(e.target.value)}
                                disabled={loading}
                            >
                                <option value="">Select a plant...</option>
                                {plants.map(plant => (
                                    <option key={plant.plant_id} value={plant.plant_id}>
                                        {plant.plant_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="reportEndingDate">Ending (Saturday):</label>
                            <input
                                type="date"
                                id="reportEndingDate"
                                value={endingDate}
                                onChange={handleEndingDateChange}
                                disabled={loading}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {message.text && (
                <div className={`message message-${message.type}`}>
                    {message.text}
                </div>
            )}

            {loading ? (
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading report data...</p>
                </div>
            ) : reportData.length > 0 ? (
                <div className="report-content">
                    <h3 className="plant-title">{selectedPlantName}</h3>
                    <p className="report-subtitle">Report Calculation Area</p>

                    <div className="table-container">
                        <table className="report-table">
                            <thead>
                                <tr>
                                    <th>Week</th>
                                    <th>Week Ending</th>
                                    <th>Clean LBS</th>
                                    <th>Soil LBS</th>
                                    {/* <th>Revenue</th> */}{/* ⚠️ Hidden — uncomment to restore */}
                                    <th>Direct Hours</th>
                                    <th>Direct Labor $</th>
                                    <th>Total Hours</th>
                                    <th>Total Labor $</th>
                                    <th>OT Hours</th>
                                    <th>Packroom FTE</th>
                                    <th>Linen Distr. FTE</th>
                                    <th>PM Complete</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.map((row, index) => (
                                    <tr
                                        key={row.report_id ?? `empty-${index}`}
                                        // ─────────────────────────────────────────
                                        // The current week row gets a CSS class so
                                        // it can be styled differently if needed.
                                        // Totals/averages do NOT include this row.
                                        // ─────────────────────────────────────────
                                        className={row.is_current_week ? 'current-week-row' : ''}
                                    >
                                        {/* 
                                            Week number counting:
                                            - Current week shows as blank (it is not counted)
                                            - Completed weeks are numbered 1–13 from oldest to newest
                                        */}
                                        <td>
                                            {row.is_current_week
                                                ? '-'
                                                : completedWeeks.length - completedWeeks.indexOf(row)
                                            }
                                        </td>
                                        <td>{formatDateForDisplay(row.week_ending)}</td>
                                        <td>{formatNumber(row.clean_lbs, 0)}</td>
                                        <td>{formatNumber(row.soil_lbs, 0)}</td>
                                        {/* <td>{formatNumber(row.revenue, 0)}</td> */}{/* ⚠️ Hidden — uncomment to restore */}
                                        <td>{formatNumber(row.direct_hours, 0)}</td>
                                        <td>{formatNumber(row.direct_labor, 0)}</td>
                                        <td>{formatNumber(row.total_hours, 0)}</td>
                                        <td>{formatNumber(row.total_labor, 0)}</td>
                                        <td>{formatNumber(row.total_ot_hours, 0)}</td>
                                        <td>{formatNumber(row.packroom_fte, 0)}</td>
                                        <td>{formatNumber(row.linen_dist_fte, 0)}</td>
                                        <td>{renderPmCell(row.pm_tickets_completed, row.pm_tickets_total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                {/* Totals row — based on 13 completed weeks only */}
                                <tr className="totals-row">
                                    <td colSpan="2"><strong>13 wk Total</strong></td>
                                    <td><strong>{totals ? formatNumber(totals.clean_lbs, 0)    : '-'}</strong></td>
                                    <td><strong>{totals ? formatNumber(totals.soil_lbs, 0)     : '-'}</strong></td>
                                    {/* <td><strong>{totals ? formatNumber(totals.revenue, 0) : '-'}</strong></td> */}{/* ⚠️ Hidden — uncomment to restore */}
                                    <td><strong>{totals ? formatNumber(totals.direct_hours, 0) : '-'}</strong></td>
                                    <td><strong>{totals ? formatNumber(totals.direct_labor, 0) : '-'}</strong></td>
                                    <td><strong>{totals ? formatNumber(totals.total_hours, 0)  : '-'}</strong></td>
                                    <td><strong>{totals ? formatNumber(totals.total_labor, 0)  : '-'}</strong></td>
                                    <td><strong>{totals ? formatNumber(totals.ot_hours, 0)     : '-'}</strong></td>
                                    <td></td>
                                    <td></td>
                                    <td><strong>{totals ? renderPmCell(totals.pm_completed, totals.pm_total) : '-'}</strong></td>
                                </tr>
                                {/* Averages row — based on 13 completed weeks only */}
                                <tr className="averages-row">
                                    <td colSpan="2"><strong>13 wk Avg</strong></td>
                                    <td><strong>{averages ? formatNumber(averages.clean_lbs, 0)    : '-'}</strong></td>
                                    <td><strong>{averages ? formatNumber(averages.soil_lbs, 0)     : '-'}</strong></td>
                                    {/* <td><strong>{averages ? formatNumber(averages.revenue, 0) : '-'}</strong></td> */}{/* ⚠️ Hidden — uncomment to restore */}
                                    <td><strong>{averages ? formatNumber(averages.direct_hours, 0) : '-'}</strong></td>
                                    <td><strong>{averages ? formatNumber(averages.direct_labor, 0) : '-'}</strong></td>
                                    <td><strong>{averages ? formatNumber(averages.total_hours, 0)  : '-'}</strong></td>
                                    <td><strong>{averages ? formatNumber(averages.total_labor, 0)  : '-'}</strong></td>
                                    <td><strong>{averages ? formatNumber(averages.ot_hours, 0)     : '-'}</strong></td>
                                    <td></td>
                                    <td></td>
                                    <td><strong>{averages && averages.pm_completion_pct !== null
                                        ? `${averages.pm_completion_pct.toFixed(1)}%`
                                        : '-'}</strong></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="empty-state">
                    <p>No data available. Please enter data in the Data Entry tab first.</p>
                </div>
            )}
        </div>
    );
}

export default FlashReportView;